import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingStep from '../components/OnboardingStep';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

const PlaceOfBirthNew = () => {
  const navigate = useNavigate();
  const [place, setPlace] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const handleLocationSearch = async (value) => {
    setPlace(value);
    
    if (value.length < 3) {
      setSuggestions([]);
      return;
    }
    
    setIsLoading(true);
    
    try {
      let data;
      const platform = Capacitor.getPlatform();
      
      // For Android/iOS, use backend proxy first, then direct API as fallback
      if (platform === 'android' || platform === 'ios') {
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/location/search?q=${encodeURIComponent(value)}`
          );
          const results = await response.json();
          if (Array.isArray(results) && results.length > 0) {
            setSuggestions(results.map(r => ({
              id: r.id || Math.random(),
              name: r.name,
              admin1: r.state || r.admin1,
              country: r.country || 'India',
              latitude: r.lat || r.latitude,
              longitude: r.lon || r.longitude,
              timezone: r.timezone || 'Asia/Kolkata'
            })));
            setIsLoading(false);
            return;
          }
        } catch (proxyError) {
          console.log('Backend proxy failed, trying direct API...');
        }
        
        // Fallback: direct fetch to open-meteo (public API, no CORS issues)
        const response = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(value)}&count=5&language=en&format=json`
        );
        data = await response.json();
      } else {
        // Web - use regular fetch
        const response = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(value)}&count=5&language=en&format=json`
        );
        data = await response.json();
      }
      
      if (data && data.results) {
        setSuggestions(data.results);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Location search error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const selectLocation = (loc) => {
    setSelectedLocation(loc);
    setPlace(loc.name);
    setSuggestions([]);
    
    localStorage.setItem('birthCoordinates', JSON.stringify({
      latitude: loc.latitude,
      longitude: loc.longitude,
      place_name: `${loc.name}, ${loc.country}`,
      timezone: loc.timezone || 'UTC',
    }));
    localStorage.setItem('placeOfBirth', `${loc.name}, ${loc.country}`);
  };
  
  const handleContinue = () => {
    if (!selectedLocation) {
      toast.error('Please select a location from the suggestions');
      return;
    }
    
    navigate('/otp-verification');
  };
  
  return (
    <OnboardingStep
      step={4}
      totalSteps={5}
      title="Where were you born?"
      reassurance="We'll find the exact coordinates"
      trustNote="Location determines your local sidereal time and planetary positions."
      onContinue={handleContinue}
      continueDisabled={!selectedLocation}
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-stone-700 mb-2 block">
            City, Country
          </span>
          <div className="relative">
            <input
              type="text"
              placeholder="Start typing..."
              className="w-full px-4 py-4 rounded-input bg-stone-50 border-2 border-transparent focus:border-cosmic-500 focus:bg-white transition-all text-lg outline-none"
              value={place}
              onChange={(e) => handleLocationSearch(e.target.value)}
              autoComplete="off"
            />
            {isLoading && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="w-5 h-5 border-2 border-cosmic-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        </label>
        
        {/* Location Suggestions */}
        {suggestions.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {suggestions.map((loc) => (
              <button
                key={loc.id}
                onClick={() => selectLocation(loc)}
                className="w-full p-3 text-left rounded-input hover:bg-stone-100 transition-colors border border-stone-200"
              >
                <div className="font-medium text-stone-900">{loc.name}</div>
                <div className="text-sm text-stone-600">
                  {loc.admin1 && `${loc.admin1}, `}{loc.country}
                </div>
              </button>
            ))}
          </div>
        )}
        
        {selectedLocation && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-input">
            <div className="text-sm text-green-800">
              ✓ Selected: {selectedLocation.name}, {selectedLocation.country}
            </div>
          </div>
        )}
      </div>
    </OnboardingStep>
  );
};

export default PlaceOfBirthNew;